import React, { useEffect, useRef, useState } from "react";
import classes from "../styles.module.scss";
import {
  ArrowRightLongIcon,
  ChevronRightIcon,
  PlacemarkIcon,
} from "../../../../assets/icons";
import { useKato } from "../../../../contexts/kato.context";
import {
  IKatoItem,
  IKatoTreeItem,
  KatoType,
} from "../../../../types/kato.types";
import { useTranslation } from "react-i18next";
import { getKatoType, getLang } from "../../../../helpers/utils";
import { useNavigate } from "react-router-dom";
import { oblast } from "../../../../consts/kato.conts";

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  query?: string;
  isVertical?: boolean;
  setQuery?: (query: string) => void;
}

const RegionsTree = ({
  isOpen,
  setIsOpen,
  query = "",
  isVertical = false,
  setQuery = () => {},
}: IProps) => {
  const { t } = useTranslation();
  const {
    katoTree,
    setSelectedRegion,
    setSelectedDistrict,
    setSelectedVillage,
    setCurrentLevel,

  } = useKato();
  const navigate = useNavigate();
  const [hoveredRegion, setHoveredRegion] = useState<IKatoItem | null>(null);
  const [hoveredDistrict, setHoveredDistrict] = useState<IKatoItem | null>(
    null
  );
  const [hoveredVillage, setHoveredVillage] = useState<IKatoItem | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Закрытие по клику вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Начальное состояние
  useEffect(() => {
    if (katoTree.length > 0) {
      const region = katoTree[0];
      const district = region.children?.[0];
      const village = district?.children?.[0];
      setHoveredRegion(region);
      setHoveredDistrict(district || null);
      setHoveredVillage(village || null);
    }
  }, [katoTree]);

  const handleSelect = (item: IKatoItem) => {
    const level = getKatoType(item.kato);
    setCurrentLevel(level);
    if (level === KatoType.REGION) {
      setSelectedRegion(item);
    } else if (level === KatoType.DISTRICT) {
      setSelectedDistrict(item);
      setSelectedRegion(hoveredRegion);
    } else if (level === KatoType.VILLAGE) {
      setSelectedVillage(item);
      setSelectedDistrict(hoveredDistrict);
      setSelectedRegion(hoveredRegion);
    }
    navigate(`/${item.kato}/indicators`);
    setIsOpen(false);
    setQuery("");
  };

  const handleHoverRegion = (region: IKatoItem) => {
    setHoveredRegion(region);
    setHoveredDistrict(null);
    setHoveredVillage(null);
  };

  const handleHoverDistrict = (district: IKatoItem) => {
    setHoveredDistrict(district);
    setHoveredVillage(null);
  };

  const matchesRegion = (region: IKatoTreeItem) =>
    region.children?.some((d) =>
      d.children?.some((v) =>
        v[`name${getLang()}`].trim().replace(/\s+/g, ' ').toLowerCase().includes(query.toLowerCase())
      )
    );

  const matchesDistrict = (district: IKatoTreeItem) =>
    district.children?.some((v) =>
      v[`name${getLang()}`].trim().replace(/\s+/g, ' ').toLowerCase().includes(query.toLowerCase())
    );

  const matchesVillage = (village: IKatoTreeItem) =>
    village[`name${getLang()}`].trim().replace(/\s+/g, ' ').toLowerCase().includes(query.toLowerCase());

  useEffect(() => {
    hoveredRegion && console.log(hoveredRegion?.children?.filter(matchesDistrict));
  }, [hoveredRegion]);

  return (
    <div className={classes.search} ref={containerRef}>
      {isOpen && !isVertical && (
        <div className={classes.search__dropdown}>
          <div className={classes.search__dropdown__header}>
            <span>{t("search.header")}</span>
            <span onClick={() => handleSelect(oblast)}>
              {t("search.of-oblast")}
            </span>
          </div>

          <div className={classes.search__dropdown__path}>
            <span>{hoveredRegion?.[`name${getLang()}`]}</span>
            {hoveredDistrict && (
              <>
                <ArrowRightLongIcon />
                <span>{hoveredDistrict?.[`name${getLang()}`]}</span>
              </>
            )}

            {hoveredVillage && (
              <>
                <ArrowRightLongIcon />
                <span>{hoveredVillage?.[`name${getLang()}`]}</span>
              </>
            )}
          </div>
          <div className={classes.search__columns}>
            {/* Regions */}
            <div className={classes.search__column}>
              {katoTree.filter(matchesRegion).sort((a, b) => a[`name${getLang()}`].trim().replace(/\s+/g, ' ').localeCompare(b[`name${getLang()}`].trim().replace(/\s+/g, ' '))).map((region) => (
                <div
                  className={classes.search__column__item}
                  key={region.kato}
                  onMouseEnter={() => handleHoverRegion(region)}
                >
                  <span onClick={() => handleSelect(region)}>
                    <PlacemarkIcon /> {region[`name${getLang()}`]}
                  </span>
                  <ChevronRightIcon />
                </div>
              ))}
            </div>

            {/* Districts */}
            <div className={classes.search__column}>
              {hoveredRegion?.children
                ?.filter(matchesDistrict)
                .sort((a: IKatoItem, b: IKatoItem) => a[`name${getLang()}`].trim().replace(/\s+/g, ' ').localeCompare(b[`name${getLang()}`].trim().replace(/\s+/g, ' ')))
                .map((district: IKatoTreeItem) => (
                  <div
                    className={classes.search__column__item}
                    key={district.kato}
                    onMouseEnter={() => handleHoverDistrict(district)}
                  >
                    <span onClick={() => handleSelect(district)}>
                      <PlacemarkIcon /> {district[`name${getLang()}`]}
                    </span>
                    <ChevronRightIcon />
                  </div>
                ))}
            </div>

            {/* Villages */}
            <div className={classes.search__column}>
              {hoveredDistrict?.children
                ?.filter(matchesVillage)
                .sort((a: IKatoItem, b: IKatoItem) => a[`name${getLang()}`].trim().replace(/\s+/g, ' ').localeCompare(b[`name${getLang()}`].trim().replace(/\s+/g, ' ')))
                .map((village: IKatoTreeItem) => (
                  <div
                    className={classes.search__column__item}
                    key={village.kato}
                    onMouseEnter={() => setHoveredVillage(village)}
                  >
                    <span onClick={() => handleSelect(village)}>
                      <PlacemarkIcon /> {village[`name${getLang()}`]}
                    </span>
                    {/* <ChevronRightIcon /> */}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {isOpen && isVertical && (
        <div className={classes.search__vertical}>
          <div
            onClick={() => handleSelect(oblast)}
            className={`${classes.search__vertical__item} ${classes.search__vertical__item__region}`}
          >
            <PlacemarkIcon /> {oblast[`name${getLang()}`]}
          </div>
          {katoTree.filter(matchesRegion).map((region) => (
            <>
              <div
                onClick={() => handleSelect(region)}
                className={`${classes.search__vertical__item} ${classes.search__vertical__item__region}`}
              >
                <PlacemarkIcon /> {region[`name${getLang()}`]}
              </div>
              {region.children
                ?.filter(matchesDistrict)
                .map((district: IKatoTreeItem) => (
                  <div>
                    <div
                      className={`${classes.search__vertical__item} ${classes.search__vertical__item__district}`}
                      key={district.kato}
                      onClick={() => handleSelect(district)}
                    >
                      <PlacemarkIcon /> {district[`name${getLang()}`]}
                    </div>
                    {district?.children
                      ?.filter(matchesVillage)
                      .map((village: IKatoTreeItem) => (
                        <div
                          className={`${classes.search__vertical__item} ${classes.search__vertical__item__village}`}
                          key={village.kato}
                          onClick={() => handleSelect(village)}
                        >
                          <PlacemarkIcon /> {village[`name${getLang()}`]}
                        </div>
                      ))}
                  </div>
                ))}
            </>
          ))}
        </div>
      )}
    </div>
  );
};

export default RegionsTree;
